/**
 * Bridge subscriber: bus events → signals.
 *
 * INVARIANT — initialization order:
 *   main.ts MUST call startSubscriber() BEFORE constructing `new Phaser.Game(...)`.
 *   This guarantees the very first `scene:enter:BOOT` event has a registered
 *   subscriber. See spec §3.1.
 *
 * INVARIANT — direction:
 *   This module is the ONLY consumer of gameBus. DOM never imports gameBus
 *   to emit. ESLint blocks DOM → Phaser imports separately.
 */
import { gameBus, type GameEvent, type SceneEnterPayload, type PlayState, type CalloutMode } from '@/bridge/gameBus';
import { gameSignals } from '@/bridge/gameSignals';
import { isZone } from '@/lib/zones';
import type { GameMode } from '@/lib/game-mode';

const VALID_SCENES = new Set(['BOOT', 'MENU', 'PLAY', 'RESULT']);
const VALID_MODES = new Set<GameMode>(['daily', 'daily-hard', 'practice']);

function isSceneEnter(p: unknown): p is SceneEnterPayload {
  if (typeof p !== 'object' || p === null) return false;
  const o = p as Record<string, unknown>;
  return typeof o['scene'] === 'string' && VALID_SCENES.has(o['scene'] as string)
    && typeof o['date'] === 'string'
    && typeof o['puzzleNumber'] === 'number';
}

function resetSceneScoped(): void {
  gameSignals.shotIndex.value = 0;
  gameSignals.playState.value = null;
  gameSignals.lastShotOutcome.value = null;
  gameSignals.record.value = null;
  gameSignals.streak.value = null;
  gameSignals.archetype.value = null;
  gameSignals.observations.value = { LF: 0, CF: 0, RF: 0, LA: 0, CA: 0, RA: 0 };
  gameSignals.pulseZone.value = null;
  gameSignals.cardMode.value = 'preplay';
  gameSignals.todayAverage.value = null;
  gameSignals.todayPlayerCount.value = null;
  gameSignals.hideHeatmap.value = false;
  gameSignals.assistAvailable.value = false;
  gameSignals.assistHint.value = null;
  gameSignals.gameMode.value = 'daily';
  gameSignals.practiceRecord.value = null;
}

function applySceneEnter(p: SceneEnterPayload): void {
  resetSceneScoped();
  gameSignals.currentScene.value = p.scene;
  gameSignals.dateKey.value = p.date;
  gameSignals.puzzleNumber.value = p.puzzleNumber;
  if (p.scene === 'MENU' || p.scene === 'PLAY' || p.scene === 'RESULT') {
    gameSignals.archetype.value = p.archetype;
  }
  if (p.scene === 'PLAY' || p.scene === 'RESULT') {
    gameSignals.record.value = p.record;
    gameSignals.shotIndex.value = p.record.shots.length;
  }
  if (p.scene === 'RESULT') {
    gameSignals.streak.value = p.streak;
  }
}

function handle(e: GameEvent): void {
  try {
    if (e.type === 'scene:enter') {
      if (!isSceneEnter(e.payload)) {
        console.error('[subscribe] malformed scene:enter payload', e.payload);
        return;
      }
      applySceneEnter(e.payload);
      return;
    }
    if (e.type === 'play:state') {
      const valid: PlayState[] = ['IDLE', 'AIMING', 'RESOLVING', 'BETWEEN_SHOTS', 'COMPLETE'];
      if (!valid.includes(e.payload.state)) {
        console.error('[subscribe] invalid play:state', e.payload);
        return;
      }
      gameSignals.playState.value = e.payload.state;
      gameSignals.shotIndex.value = e.payload.shotIndex;
      return;
    }
    if (e.type === 'play:shot-resolved') {
      if (typeof e.payload.index !== 'number' || !e.payload.record || !e.payload.shot) {
        console.error('[subscribe] malformed play:shot-resolved', e.payload);
        return;
      }
      // §4.2 share-leak fix: in practice, route the resolved record to the
      // isolated practiceRecord signal and leave the authoritative daily
      // `record` untouched. The share grid (ShareModal.buildShareText) reads
      // `record`, never `practiceRecord`.
      if (gameSignals.gameMode.value === 'practice') {
        gameSignals.practiceRecord.value = e.payload.record;
      } else {
        gameSignals.record.value = e.payload.record;
        // First shot of the round — flip card from preplay → live (spec §4.7).
        if (e.payload.record.shots.length === 1) {
          gameSignals.cardMode.value = 'live';
        }
      }
      gameSignals.shotIndex.value = e.payload.record.shots.length;
      gameSignals.lastShotOutcome.value = e.payload.shot.outcome;
      return;
    }
    if (e.type === 'play:observations-update') {
      const obs = e.payload?.observations;
      if (typeof e.payload !== 'object' || e.payload === null || typeof obs !== 'object' || obs === null) {
        console.error('[subscribe] malformed play:observations-update', e.payload);
        return;
      }
      gameSignals.observations.value = obs;
      return;
    }
    if (e.type === 'play:pulse-zone') {
      const zone = e.payload?.zone;
      if (zone !== null && (typeof zone !== 'string' || !isZone(zone))) {
        console.error('[subscribe] malformed play:pulse-zone', e.payload);
        return;
      }
      gameSignals.pulseZone.value = zone;
      return;
    }
    if (e.type === 'play:card-mode') {
      const validModes: CalloutMode[] = ['preplay', 'live', 'truth'];
      if (!validModes.includes(e.payload.mode)) {
        console.error('[subscribe] malformed play:card-mode', e.payload);
        return;
      }
      gameSignals.cardMode.value = e.payload.mode;
      return;
    }
    if (e.type === 'play:mode') {
      const { mode, hideHeatmap } = e.payload;
      if (!VALID_MODES.has(mode as GameMode) || typeof hideHeatmap !== 'boolean') {
        console.error('[subscribe] malformed play:mode', e.payload);
        return;
      }
      gameSignals.gameMode.value = mode;
      gameSignals.hideHeatmap.value = hideHeatmap;
      return;
    }
    if (e.type === 'play:assist') {
      const { available, hint } = e.payload;
      if (typeof available !== 'boolean' || (hint !== null && (typeof hint !== 'string' || !isZone(hint)))) {
        console.error('[subscribe] malformed play:assist', e.payload);
        return;
      }
      gameSignals.assistAvailable.value = available;
      gameSignals.assistHint.value = hint;
      return;
    }
  } catch (err) {
    console.error('[subscribe] handler threw', err);
  }
}

export function startSubscriber(): () => void {
  return gameBus.subscribe(handle);
}
